import React, { useEffect, useState } from 'react';
import styled from 'styled-components';
import { FaTrash, FaMapMarkedAlt, FaEdit, FaPlus, FaEye } from 'react-icons/fa';


function PolygonListModal({ isOpen, onClose, mouseCoordinates, onGetLocation, offGetLocation, polygonCoordsDB, setPolygonCoordsDB }) {
  const [editingIndex, setEditingIndex] = useState(null);
  const [currentPolygon, setCurrentPolygon] = useState(null); // Store the full polygon object
  const [newLat, setNewLat] = useState('');
  const [newLng, setNewLng] = useState('');
  const [clickCoordinates, setClickCoordinates] = useState(mouseCoordinates || false);
  const [mouseIndex, setMouseIndex] = useState(0);
  

  const handleDeletePolygon = (index) => {
    setPolygonCoordsDB((prevCoords) => prevCoords.filter((_, i) => i !== index));
  };

  const handleEditPolygon = (index) => {
    const polygonToEdit = polygonCoordsDB[index];
    setEditingIndex(index);
    setCurrentPolygon(polygonToEdit ? { ...polygonToEdit } : null); // Ensure polygon is not null
  };
  

  // Close edit modal
  const handleEditModalClose = () => {
    setEditingIndex(null);
    setCurrentPolygon(null);
  };

  // Handle delete coordinate
  const handleDeleteCoordinate = (coordIndex) => {
    const updatedCoords = currentPolygon.coords.filter((_, i) => i !== coordIndex);
    setCurrentPolygon({ ...currentPolygon, coords: updatedCoords });
    updatePolygonCoordinates(editingIndex, updatedCoords);
  };

  // Handle adding a coordinate
  const handleAddCoordinate = () => {
      const updatedCoords = [...currentPolygon.coords, { lat: parseFloat(newLat), lng: parseFloat(newLng), radius: 0 }];
      setCurrentPolygon({
        ...currentPolygon,
        coords: updatedCoords,
      });
      updatePolygonCoordinates(editingIndex, updatedCoords);
      setNewLat('');
      setNewLng('');
  };
  
  

  // Update polygon coordinates
  const updatePolygonCoordinates = (polygonIndex, updatedCoords) => {
    const updatedPolygons = [...polygonCoordsDB];
    updatedPolygons[polygonIndex].coords = updatedCoords;
    setPolygonCoordsDB(updatedPolygons);
  };
  
  const handleSavePolygonEdit = () => {
    if (currentPolygon && editingIndex !== null) {
      const updatedPolygons = [...polygonCoordsDB];
      updatedPolygons[editingIndex] = currentPolygon;
      setPolygonCoordsDB(updatedPolygons);
      handleEditModalClose(); // Close the modal after saving
    }
  };
  

  const handleInputChange = (index, field, value) => {
    if (currentPolygon && currentPolygon.coords && currentPolygon.coords.length > index) {
      const updatedCoords = [...currentPolygon.coords];
      updatedCoords[index] = {
        ...updatedCoords[index],
        [field]: value,  // Update the specific lat/lng/radius field
      };
      setCurrentPolygon((prevPolygon) => ({
        ...prevPolygon,
        coords: updatedCoords,
      }));
    }
  };

  const handleOptionChange = (optionField, value) => {
    if (optionField === 'type') {
      setCurrentPolygon((prevPolygon) => ({
        ...prevPolygon,
        [optionField]: value,
      }));
    } else {
      const updatedOptions = {
        ...currentPolygon.options,
        [optionField]: value,
      };
  
      if (optionField === 'radiusEnabled' && value) {
        const updatedCoords = currentPolygon.coords.map((coord) => ({
          ...coord,
          radius: coord.radius || 0,
        }));
        setCurrentPolygon((prevPolygon) => ({
          ...prevPolygon,
          options: updatedOptions,
          coords: updatedCoords,
        }));
      } else {
        setCurrentPolygon((prevPolygon) => ({
          ...prevPolygon,
          options: updatedOptions,
        }));
      }
    }
  };  

  useEffect(() => {
    if (mouseCoordinates?.lat && mouseCoordinates?.lng) {
      offGetLocation();
      setClickCoordinates(mouseCoordinates);
  
      const fetchedLat = mouseCoordinates?.lat;
      const fetchedLng = mouseCoordinates?.lng;
  
      if (fetchedLat && fetchedLng) {
        const updatedCoords = [...currentPolygon.coords];
  
        console.log(updatedCoords);
  
        updatedCoords[mouseIndex] = {
          ...updatedCoords[mouseIndex],
          lat: fetchedLat,
          lng: fetchedLng,
        };
  
        setCurrentPolygon({ ...currentPolygon, coords: updatedCoords });
        setNewLat(fetchedLat);
        setNewLng(fetchedLng);
      }
    }
  }, [mouseCoordinates]);


  const handleGetLocation = (coordIndex) => {
    onGetLocation(coordIndex);
    setMouseIndex(coordIndex);
  };
  

  return (
    <ModalContainer isOpen={isOpen}>
      <ModalContent>
        <ModalHeader>
          <h2>Polygon/Line List</h2>
          <CloseButton onClick={onClose}>×</CloseButton>
        </ModalHeader>
        <ModalBody>
          {polygonCoordsDB.length === 0 ? (
            <NoPolygons>No polygons or lines to display.</NoPolygons>
          ) : (
            <Table>
              <thead>
                <tr>
                  <th>ID</th>
                  <th>Name</th>
                  <th>Type</th>
                  <th>Latitude</th>
                  <th>Longitude</th>
                  <th>Options</th>
                </tr>
              </thead>
              <tbody>
                {polygonCoordsDB.map((polygon, index) => (
                  <tr key={index}>
                    <td>{index + 1}</td>
                    <td>{polygon.options.name || 'Unnamed'}</td>
                    <td>{polygon.options.type === 'line' ? 'Line' : 'Polygon'}</td>
                    <td>
                      {polygon.coords && polygon.coords.length > 0 ? (
                        polygon.coords.map((coord, idx) => (
                          <CoordinateWrapper key={`lat-${idx}`}>
                            <div>{coord.lat}</div> {/* Render lat */}
                            {idx < polygon.coords.length - 1 && <Separator />} {/* Add separator between coordinates */}
                          </CoordinateWrapper>
                        ))
                      ) : (
                        <span>Invalid data</span>
                      )}
                    </td>
                    <td>
                      {polygon.coords && polygon.coords.length > 0 ? (
                        polygon.coords.map((coord, idx) => (
                          <CoordinateWrapper key={`lng-${idx}`}>
                            <div>{coord.lng}</div> {/* Render lng */}
                            {idx < polygon.coords.length - 1 && <Separator />} {/* Add separator between coordinates */}
                          </CoordinateWrapper>
                        ))
                      ) : (
                        <span>Invalid data</span>
                      )}
                    </td>
                    <td>
                      <OptionsCell>
                        <ActionButtonView variant="primary">
                          <FaEye />
                        </ActionButtonView>
                        <OptionsPopup>
                          <div>Fill Color: {polygon.options.fillColor}</div>
                          <div>Stroke Color: {polygon.options.strokeColor}</div>
                          <div>Stroke Weight: {polygon.options.strokeWeight}</div>
                          <div>Stroke Opacity: {polygon.options.strokeOpacity}</div>
                          <div>Filled: {polygon.options.isFilled ? 'Yes' : 'No'}</div>
                          <div>CircleEnabled: {polygon.options.radiusEnabled ? 'Yes' : 'No'}</div>
                          {polygon.options.radiusEnabled && (
                            <>
                              <div>Circle Stroke Color: {polygon.options.circleStrokeColor}</div>
                              <div>Circle Fill Color: {polygon.options.circleFillColor}</div>
                            </>
                          )}
                        </OptionsPopup>
                        <ActionButton variant="success" onClick={() => handleDeletePolygon(index)}>
                          <FaTrash /> {/* Trash Icon */}
                        </ActionButton>
                        <ActionButton variant="secondary" onClick={() => handleEditPolygon(index)}>
                          <FaEdit /> {/* Edit Icon */}
                        </ActionButton>
                      </OptionsCell>
                    </td>
                  </tr>
                ))}
              </tbody>
            </Table>
          )}
        </ModalBody>
      </ModalContent>

      {(isOpen && editingIndex >= 0 && currentPolygon?.coords?.length > 0) && (
        <EditModal>
          <ModalHeader>
            <h2>Edit Polygon/Line</h2>
            <CloseButton onClick={handleEditModalClose}>×</CloseButton>
          </ModalHeader>
          <ModalBody>
            <CoordinateTableContainer>
              <CoordinateTable>
                <thead>
                  <tr>
                    <th>Latitude</th>
                    <th>Longitude</th>
                    {currentPolygon.options.radiusEnabled && <th>Radius (m)</th>}
                    <th>Action</th>
                  </tr>
                </thead>
                <tbody>
                  {currentPolygon.coords.map((coord, index) => (
                    <tr key={index}>
                      <td>
                        <CoordinateInput
                          type="number"
                          value={coord.lat}
                          onChange={(e) => handleInputChange(index, 'lat', parseFloat(e.target.value))}
                        />
                      </td>
                      <td>
                        <CoordinateInput
                          type="number"
                          value={coord.lng}
                          onChange={(e) => handleInputChange(index, 'lng', parseFloat(e.target.value))}
                        />
                      </td>
                      {currentPolygon.options.radiusEnabled && (
                        <td>
                          <CoordinateInput
                            type="number"
                            value={coord.radius !== undefined ? coord.radius : 0}  // undefined일 때만 기본값으로 1 설정
                            onChange={(e) => handleInputChange(index, 'radius', parseFloat(e.target.value))}
                          />
                        </td>
                      )}
                      <td>
                        <div style={{ display: 'flex', gap: '1px', border: "0 !important", alignContent: "center", justifyContent: "center", position: "relative", top: "2px" }}>
                          <ActionButton onClick={() => handleDeleteCoordinate(index)}>
                            <FaTrash />
                          </ActionButton>
                          <ActionButton onClick={() => handleGetLocation(index)}>
                            <FaMapMarkedAlt />
                          </ActionButton>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </CoordinateTable>
            </CoordinateTableContainer>
            <AddCoordinateButton onClick={handleAddCoordinate}>
              <FaPlus /> Add Coordinate {/* Plus icon */}
            </AddCoordinateButton>

            <OptionsSection>
              <OptionGroup>
                <OptionLabel>Name:</OptionLabel>
                <OptionInput
                  type="text"
                  value={currentPolygon.options.name}
                  onChange={(e) => handleOptionChange('name', e.target.value)}
                />
              </OptionGroup>
              <OptionGroup>
                <OptionLabel>Shape Type:</OptionLabel>
                <OptionInput
                  as="select"
                  value={currentPolygon?.type || 'polygon'}  // Show current type, default to 'polygon' only if type is undefined
                  onChange={(e) => handleOptionChange('type', e.target.value)}  // Update type on change
                >
                  <option value="polygon">Polygon</option>
                  <option value="line">Line</option>
                </OptionInput>
              </OptionGroup>


              <OptionGroup>
                <OptionLabel>Fill Color:</OptionLabel>
                <OptionInput
                  type="color"
                  value={currentPolygon.options.fillColor}
                  onChange={(e) => handleOptionChange('fillColor', e.target.value)}
                />
              </OptionGroup>
              <OptionGroup>
                <OptionLabel>Stroke Color:</OptionLabel>
                <OptionInput
                  type="color"
                  value={currentPolygon.options.strokeColor}
                  onChange={(e) => handleOptionChange('strokeColor', e.target.value)}
                />
              </OptionGroup>
              <OptionGroup>
                <OptionLabel>Stroke Opacity:</OptionLabel>
                <OptionInput
                  type="number"
                  value={currentPolygon.options.strokeOpacity}
                  onChange={(e) => handleOptionChange('strokeOpacity', e.target.value)}
                />
              </OptionGroup>
              <OptionGroup>
                <OptionLabel>Stroke Weight:</OptionLabel>
                <OptionInput
                  type="number"
                  value={currentPolygon.options.strokeWeight}
                  onChange={(e) => handleOptionChange('strokeWeight', e.target.value)}
                />
              </OptionGroup>

              <OptionGroup>
                <OptionLabel>Enable Radius:</OptionLabel>
                <OptionInput
                  type="checkbox"
                  checked={currentPolygon.options.radiusEnabled || false}
                  onChange={(e) => handleOptionChange('radiusEnabled', e.target.checked)}
                />
              </OptionGroup>

              {currentPolygon.options.radiusEnabled && (
                <>
                  <OptionGroup>
                    <OptionLabel>Circle Stroke Color:</OptionLabel>
                    <OptionInput
                      type="color"
                      value={currentPolygon.options.circleStrokeColor}
                      onChange={(e) => handleOptionChange('circleStrokeColor', e.target.value)}
                    />
                  </OptionGroup>
                  <OptionGroup>
                    <OptionLabel>Circle Fill Color:</OptionLabel>
                    <OptionInput
                      type="color"
                      value={currentPolygon.options.circleFillColor}
                      onChange={(e) => handleOptionChange('circleFillColor', e.target.value)}
                    />
                  </OptionGroup>
                </>
              )}
            </OptionsSection>

            <ButtonGroup>
              <ModalButton onClick={handleSavePolygonEdit}>Save</ModalButton>
              <CancelButton onClick={handleEditModalClose}>Cancel</CancelButton>
            </ButtonGroup>
          </ModalBody>
        </EditModal>
      )}
    </ModalContainer>
  );
}

const AddCoordinateButton = styled.button`
  background-color: #28a745;
  color: white;
  border: none;
  padding: 10px 20px;
  border-radius: 5px;
  cursor: pointer;
  font-size: 16px;
  font-weight: 500;
  transition: background-color 0.3s, transform 0.2s;
  margin-top: 10px;
  display: block;
  width: 100%;
  &:hover {
    background-color: #218838;
  }
  &:active {
    background-color: #1e7e34;
    transform: scale(0.98);
  }
`;

const CoordinateTableContainer = styled.div`
  max-height: 500px;
  overflow-y: auto;
  scrollbar-width: thin; /* Firefox에서 얇은 스크롤바 */
  scrollbar-color: rgba(0, 0, 0, 0.3) transparent; /* 스크롤바 색상 */

  /* Chrome, Safari, Edge의 스크롤바 스타일 */
  &::-webkit-scrollbar {
    width: 8px; /* 스크롤바의 너비 */
  }

  &::-webkit-scrollbar-track {
    background: transparent; /* 스크롤 트랙 색상 */
  }

  &::-webkit-scrollbar-thumb {
    background-color: rgba(0, 0, 0, 0.3); /* 스크롤바 색상 */
    border-radius: 10px; /* 둥근 스크롤바 */
    border: 2px solid transparent;
    background-clip: padding-box;
  }
`;

const CoordinateTable = styled.table`
  width: 100%;
  border-collapse: collapse;
  margin-bottom: 20px;

  th, td {
    border: 1px solid #ddd;
    padding: 10px;
    text-align: center;
  }

  th {
    background-color: #f4f4f4;
    font-weight: bold;
  }

  tr:nth-child(even) {
    background-color: #f9f9f9;
  }

  button {
    background-color: transparent;
    color: #007bff;
    border: none;
    padding: 5px;
    cursor: pointer;
    font-size: 18px;
  }

  button:hover {
    color: #0056b3;
  }
`;

const ModalContainer = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.5);
  display: ${(props) => (props.isOpen ? 'flex' : 'none')};
  justify-content: center;
  align-items: center;
  z-index: 1000;
`;


const EditModal = styled.div`
  background: #fff;
  z-index: 1001;
  margin-left: 20px;
  background: white;
  border-radius: 8px;
  max-width: 900px;
  max-height: 80%;
  overflow-y: auto;
  box-shadow: 0 4px 8px rgba(0, 0, 0, 0.2);
  padding: 20px;
`;


const ModalContent = styled.div`
  background: white;
  border-radius: 8px;
  width: 80%;
  max-width: 900px;
  max-height: 80%;
  min-height: 600px; /* Minimum height of 600px */
  overflow-y: auto;
  box-shadow: 0 4px 8px rgba(0, 0, 0, 0.2);
  padding: 20px;
`;


const ModalHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding-bottom: 10px;
  border-bottom: 1px solid #ddd;
`;

const CloseButton = styled.button`
  background: transparent;
  border: none;
  font-size: 24px;
  cursor: pointer;
  color: #999;
  &:hover {
    color: #000;
  }
`;

const ModalBody = styled.div`
  margin-top: 20px;
`;

const Table = styled.table`
  width: 100%;
  border-collapse: collapse;
  margin-bottom: 20px;
  
  th, td {
    border: 1px solid #ddd;
    padding: 10px;
    text-align: center;
  }

  th {
    background-color: #f4f4f4;
    font-weight: bold;
  }

  td {
    word-wrap: break-word;
    position: relative;
  }

  td > div {
    display: flex;
    gap: 5px; /* Add spacing between buttons */
    flex: 1;
    justify-content: center;
    text-align: center;
  }
`;

const OptionsCell = styled.div`
  position: relative;
  cursor: pointer;
  display: flex;
  justify-content: space-evenly; /* Ensures the buttons are spaced evenly */
  gap: 10px; /* Add spacing between buttons */
  align-items: center;
`;


const ActionButton = styled.button`
  color: white;
  border: none;
  padding: 8px 12px;
  border-radius: 4px;
  cursor: pointer;
  transition: background-color 0.3s ease;
  width: 50px; /* Ensure all buttons are of the same width */
  display: flex;
  align-items: center;
  justify-content: center;

  &:hover {
    background-color: ${({ hoverColor }) => hoverColor || '#ccc'};
  }

  /* First button (blue) */
  ${({ variant }) => variant === 'primary' && `
    background-color: #007bff;
    &:hover {
      background-color: #0056b3;
    }
  `}

  /* Second button (light red) */
  ${({ variant }) => variant === 'secondary' && `
    background-color: #ff7675;
    &:hover {
      background-color: #e17055;
    }
  `}

  /* Third button (light green) */
  ${({ variant }) => variant === 'success' && `
    background-color: #55efc4;
    &:hover {
      background-color: #00b894;
    }
  `}
`;

const ActionButtonView = styled.button`
  color: white;
  border: none;
  padding: 8px 12px;
  border-radius: 4px;
  cursor: pointer;
  transition: background-color 0.3s ease;
  width: 50px; /* Ensure all buttons are of the same width */
  display: flex;
  align-items: center;
  justify-content: center;

  &:hover {
    background-color: ${({ hoverColor }) => hoverColor || '#ccc'};
  }

  /* First button (blue) */
  ${({ variant }) => variant === 'primary' && `
    background-color: #007bff;
    &:hover {
      background-color: #0056b3;
    }
  `}

  /* Second button (light red) */
  ${({ variant }) => variant === 'secondary' && `
    background-color: #ff7675;
    &:hover {
      background-color: #e17055;
    }
  `}

  /* Third button (light green) */
  ${({ variant }) => variant === 'success' && `
    background-color: #55efc4;
    &:hover {
      background-color: #00b894;
    }
  `}
`;

const OptionsPopup = styled.div`
  display: none;
  position: absolute;
  top: 100%;
  left: 50%;
  transform: translateX(-120%);
  background: #fff;
  border-radius: 8px;
  border: 1px solid rgba(0, 0, 0, 0.1);
  padding: 15px;
  box-shadow: 0px 6px 12px rgba(0, 0, 0, 0.15);
  z-index: 1;
  width: max-content;
  transition: opacity 0.2s ease-in-out;
  opacity: 0;

  ${ActionButtonView}:hover + & {
    display: block;
    opacity: 1;
  }

  div {
    font-size: 14px;
    margin-bottom: 10px;
    color: #333;
    line-height: 1.5;
    display: flex;
    justify-content: space-between;
    align-items: center;
  }

  div:last-child {
    margin-bottom: 0;
  }

  div:hover {
    color: #007bff;
  }

  div:before {
    content: '●';
    color: #007bff;
    margin-right: 8px;
  }
`;

const NoPolygons = styled.p`
  text-align: center;
  font-size: 16px;
  color: #333;
  margin-top: 20px;
`;


const OptionsSection = styled.div`
  margin-top: 20px;
  display: flex;
  flex-direction: column;
  gap: 15px;
`;

const OptionGroup = styled.div`
  display: flex;
  flex-direction: column;
  gap: 10px;
`;

const OptionLabel = styled.label`
  display: flex;
  align-items: center;
  font-size: 14px;
  width: 150px;
  margin-bottom: 5px;
`;

const OptionInput = styled.input`
  margin-left: 10px;
  flex: 1;
  &[type="checkbox"] {
    width: 20px;
    height: 20px;
    transform: scale(1.5);
    cursor: pointer;
    margin-left: 15px;
  }
  &[type="checkbox"]:checked {
    background-color: #007bff;
    border-color: #007bff;
  }
`;

const ButtonGroup = styled.div`
  display: flex;
  justify-content: flex-end;
  gap: 15px;
  margin-top: 20px;
`;

const ModalButton = styled.button`
background-color: #007bff;
color: white;
border: none;
padding: 10px 20px;
border-radius: 5px;
cursor: pointer;
font-size: 16px;
font-weight: 500;
transition: background-color 0.3s, transform 0.2s;

&:hover {
  background-color: #0056b3;
}

&:active {
  background-color: #00408d;
  transform: scale(0.98);
}

&:disabled {
  background-color: #d6d6d6;
  color: #a1a1a1;
  cursor: not-allowed;
  transform: none;
}
`;

const CancelButton = styled(ModalButton)`
background-color: #6c757d;
&:hover {
  background-color: #5a6268;
}
&:active {
  background-color: #4e555b;
}
`;

const CoordinateInput = styled.input`
  width: 100%;
  padding: 8px;
  border-radius: 4px;
  border: 1px solid #ddd;
`;

const CoordinateWrapper = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  margin-bottom: 5px;  /* Reduced margin for tighter spacing */
`;

const Separator = styled.div`
  width: 100%;
  height: 1px;
  background-color: #ccc;
  margin: 2px 0;  /* Add a small margin between the lat/lng and the line */
`;

export default PolygonListModal;
